import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  Text,
  Button,
  Card,
  Input,
  Field,
  tokens,
  Switch,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Spinner,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../components/Common/ScrollableMessageBar';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { getRuntimeConfig } from '../config/runtimeConfig';
import {
  CloudRegular,
  KeyRegular,
  SaveRegular,
  PlugConnectedRegular,
  Delete20Regular,
  Add20Regular } from
'@fluentui/react-icons';
import { domainFeedService } from '../services/domainFeedService';
import { apiClient } from '../services/apiClient';
import { useError } from '../contexts/ErrorContext';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
    maxWidth: '1200px'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },

  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },

  subtitle: {
    fontSize: '16px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '24px'
  },

  configCard: {
    ...shorthands.padding('24px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('12px'),
    boxShadow: tokens.shadow4
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    ...shorthands.gap('20px'),
    marginBottom: '24px'
  },

  actionButtons: {
    display: 'flex',
    ...shorthands.gap('12px'),
    justifyContent: 'flex-start'
  },

  urlPreview: {
    fontFamily: 'Monaco, Menlo, "Courier New", monospace',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('6px'),
    marginTop: '8px'
  },

  modalSurface: {
    minWidth: '950px',
    maxWidth: '95vw'
  },

  modalContent: {
    width: '100%',
    maxHeight: '600px',
    overflowY: 'auto',
    ...shorthands.padding('20px')
  },

  domainTable: {
    width: '100%',
    marginTop: '16px'
  },

  domainCell: {
    fontFamily: 'Monaco, Menlo, "Courier New", monospace',
    fontSize: '13px'
  },

  domainCount: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginBottom: '16px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('6px')
  },

  restrictedDomainsSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('20px'),
    backgroundColor: '#f8f8f8',
    ...shorthands.borderRadius('8px')
  },

  domainsList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    maxHeight: '200px',
    overflowY: 'auto'
  },

  domainItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: '#ffffff',
    ...shorthands.border('1px', 'solid', '#e8e8e8'),
    ...shorthands.borderRadius('4px')
  },

  domainInput: {
    display: 'flex',
    ...shorthands.gap('8px'),
    marginTop: '8px'
  },

  warningText: {
    fontSize: '13px',
    color: '#a4262c',
    backgroundColor: '#fdf2f2',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('4px'),
    marginTop: '8px'
  },

  removeButton: {
    minWidth: '24px',
    height: '24px',
    color: '#a4262c'
  }
});

export const DomainFeedConfig: React.FC = () => {
  const styles = useStyles();
  const { showError, showWarning, showInfo } = useError();

  // State management
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useScrollableMessage<string | null>(null);
  const [success, setSuccess] = useScrollableMessage<string | null>(null);

  // Modal state
  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [feedDomains, setFeedDomains] = useState<any[]>([]);

  // Configuration state - load from centralized config
  const [feedUrl, setFeedUrl] = useState(() => {
    try {
      return getRuntimeConfig().domainFeedUrl || 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';
    } catch {
      return '';
    }
  });
  const [apiKey, setApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [autoApproveEnabled, setAutoApproveEnabled] = useState(false);
  const [allowPolicyUpdates, setAllowPolicyUpdates] = useState(false);
  const [restrictedDomains, setRestrictedDomains] = useState<string[]>(['badbank.com']);
  const [newDomain, setNewDomain] = useState('');

  // Load configuration on component mount
  // This retrieves the current config and checks if an API key exists in Key Vault
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Helper function to set API key state based on configuration
  const setApiKeyState = (hasApiKey: boolean) => {
    setHasApiKey(hasApiKey);

    // If API key exists in Key Vault, show masked placeholder
    // This provides visual feedback that a key is stored securely
    // without exposing the actual key value
    if (hasApiKey) {
      setApiKey('••••••••••••••••••••••••••••••••');
    } else {

      // Keep existing API key value if no key is stored
    }};

  // Helper function to apply auto sync settings
  const setAutoSyncSettings = (config: any) => {
    // Set auto sync based on telemetry setting
    if (config.sendTelemetry) {
      setAutoSyncEnabled(config.sendTelemetry !== 'no');
    }
  };

  // Helper function to apply domain management settings
  const setDomainManagementSettings = (config: any) => {
    // Set auto approve setting
    if (config.autoApproveDomains !== undefined) {
      setAutoApproveEnabled(config.autoApproveDomains === true);
    }

    // Set allow policy updates setting
    if (config.allowPolicyUpdates !== undefined) {
      setAllowPolicyUpdates(config.allowPolicyUpdates === true);
    }
  };

  // Helper function to process direct response format (not wrapped)
  const processDirectResponseConfig = (config: any) => {
    setApiKeyState(config.hasLsegApiKey === true);
    setAutoSyncSettings(config);
    setDomainManagementSettings(config);
  };

  // Helper function to process wrapped response format
  const processWrappedResponseConfig = (config: any) => {
    setApiKeyState(config.hasLsegApiKey || false);

    // Set auto sync based on telemetry setting
    setAutoSyncEnabled(config.sendTelemetry !== 'no');

    setDomainManagementSettings(config);
  };

  // Helper function to handle configuration loading errors
  const handleConfigurationError = (error: any) => {
    console.error('Failed to load configuration:', error);
    showError({
      message: error?.message || 'Failed to load domain feed configuration',
      title: 'Configuration Load Failed',
      severity: 'error',
      details: error?.stack,
      onRetry: () => loadConfiguration()
    });
    setError('Failed to load configuration. Using defaults.');
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<{
        success: boolean;
        data?: {
          hasLsegApiKey: boolean;
          sendTelemetry: 'receipts' | 'receipts-and-status' | 'no';
          [key: string]: any;
        };
      }>('/config');


      // Check if the response is the data directly (not wrapped)
      if ('hasLsegApiKey' in response.data) {
        const config = response.data as any;
        processDirectResponseConfig(config);
        return; // Exit early since we handled it
      }

      if (response.data.success && response.data.data) {
        const config = response.data.data;
        processWrappedResponseConfig(config);
      }
    } catch (error: any) {
      handleConfigurationError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = () => {
    if (newDomain && !restrictedDomains.includes(newDomain)) {
      setRestrictedDomains([...restrictedDomains, newDomain]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setRestrictedDomains(restrictedDomains.filter((d) => d !== domain));
  };

  /**
   * Test Domain Feed API connection using the stored API key from Key Vault
   * Flow:
   * 1. Frontend calls /api/lseg/domains with user's JWT token
   * 2. Backend validates JWT for security
   * 3. Backend retrieves Domain Feed API key from Key Vault
   * 4. Backend calls Domain Feed API with the stored key
   * 5. Returns domains to frontend for display
   */
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      setLoadingDomains(true);
      setShowDomainsModal(true);
      setFeedDomains([]); // Clear previous data

      // Call the Domain Feed domains endpoint (requires JWT authentication)
      const feedData = await domainFeedService.getFeedData('banking');


      const demoDomains = feedData.map((domain) => ({
        domain: domain.domain,
        organization: domain.organization
      }));

      setFeedDomains(demoDomains);
      setSuccess(`✅ Connection successful! Found ${demoDomains.length} domains in domain feed.`);
      showInfo(`Connection successful! Found ${demoDomains.length} domains in domain feed.`, 'Test Successful');

    } catch (err: any) {
      const errorMessage = err?.message || 'Unknown error';
      setError(`❌ Connection test failed: ${errorMessage}`);
      setFeedDomains([]);
      showError({
        message: `Connection test failed: ${errorMessage}`,
        title: 'Connection Test Failed',
        severity: 'error',
        details: err?.stack,
        onRetry: () => handleTestConnection()
      });
    } finally {
      setTesting(false);
      setLoadingDomains(false);
    }
  };

  const handleSaveConfig = async () => {
    // Check if we have a masked key (already saved) or a new key
    const isMaskedKey = apiKey && apiKey.startsWith('••••');

    if (!apiKey.trim() || isMaskedKey) {
      // If it's masked, don't try to save it again
      if (isMaskedKey && hasApiKey) {
        setSuccess('✅ API key already stored in Key Vault. Enter a new key to update.');
        return;
      }
      setError('❌ API Key is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // First, load existing configuration to preserve other settings
      const existingResponse = await apiClient.get<{
        success: boolean;
        data?: {
          securityGroup?: string;
          federationEnabled?: boolean;
          approvalMode?: string;
          notificationEmail?: string;
          restrictedDomains?: string[];
          [key: string]: any;
        };
      }>('/config');

      const existingConfig = existingResponse.data?.data || {};

      // Save API key via centralized config system, preserving existing settings
      const configData = {
        // Preserve existing config
        securityGroup: (existingConfig as any).securityGroup || 'All Directory Users',
        federationEnabled: (existingConfig as any).federationEnabled !== false,
        approvalMode: (existingConfig as any).approvalMode || 'additions-only',
        notificationEmail: (existingConfig as any).notificationEmail || 'admin@contoso.com',
        restrictedDomains: (existingConfig as any).restrictedDomains || [],
        // Update only these fields
        lsegApiKey: apiKey,
        sendTelemetry: autoSyncEnabled ? 'receipts' : 'no',
        autoApproveDomains: autoApproveEnabled,
        allowPolicyUpdates: allowPolicyUpdates
      };

      const response = await apiClient.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>('/config', configData);

      if (response.success) {
        setHasApiKey(true);
        // Don't clear the API key - keep it for testing
        setSuccess('✅ Configuration saved! API key securely stored in Key Vault.');
        showInfo('Configuration saved! API key securely stored in Key Vault.', 'Configuration Saved');
        // Reload configuration to get updated state
        await loadConfiguration();
      } else {
        const errorMessage = response.data?.error || 'Unknown error';
        setError(`❌ Failed to save: ${errorMessage}`);
        showError({
          message: `Failed to save configuration: ${errorMessage}`,
          title: 'Save Failed',
          severity: 'error'
        });
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Unknown error';
      setError(`❌ Failed to save: ${errorMessage}`);
      showError({
        message: `Failed to save configuration: ${errorMessage}`,
        title: 'Save Failed',
        severity: 'error',
        details: err?.stack,
        onRetry: () => handleSaveConfig()
      });
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className={styles.container} data-testid="domain-feed-config-container-div">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }} data-testid="domain-feed-config-loading-center-div">
          <Spinner label="Loading configuration..." data-testid="domain-feed-config-loading-configuration-spinner" />
        </div>
      </div>);

  }

  return (
    <div className={styles.container} data-testid="domain-feed-config-container-2-div">
      <div className={styles.header} data-testid="domain-feed-config-header-div">
        <Text className={styles.title} data-testid="domain-feed-config-domain-feed-configuration-text">Domain Feed Configuration</Text>
      </div>

      {/* Messages */}
      {error &&
        <MessageBar intent="error" data-testid="domain-feed-config-error-message-bar">
          <MessageBarBody data-testid="domain-feed-config-error-message-bar-body">{error}</MessageBarBody>
        </MessageBar>
      }

      {success &&
        <MessageBar intent="success" data-testid="domain-feed-config-success-message-bar">
          <MessageBarBody data-testid="domain-feed-config-success-message-bar-body">{success}</MessageBarBody>
        </MessageBar>
      }

      {/* Configuration Form */}
      <Card className={styles.configCard} data-testid="domain-feed-config-settings-card">
        <Text style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }} data-testid="domain-feed-config-feed-settings-text">
          Feed Settings
        </Text>
        
        <div className={styles.formGrid} data-testid="domain-feed-config-form-grid-div">
          <Field label="Domain Feed URL" required data-testid="domain-feed-config-domain-feed-url-field">
            <Input
              value={feedUrl}
              onChange={(_, data) => setFeedUrl(data.value)}
              placeholder={(() => {
                try {
                  return getRuntimeConfig().domainFeedUrl || 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';
                } catch {
                  return 'https://app-gateway-wsteams.workspace.lseg.com/od-service/v1/domains';
                }
              })()}
              contentBefore={<CloudRegular data-testid="domain-feed-config-cloud-regular" />}
              disabled data-testid="domain-feed-config-feed-url-input" />

            <Text className={styles.urlPreview} data-testid="domain-feed-config-lseg-open-directory-service-text">
              LSEG Open Directory Service: {feedUrl}
            </Text>
          </Field>

          <Field label="API Key" required data-testid="domain-feed-config-api-key-field">
            <Input
              type="password"
              value={apiKey}
              onChange={(_, data) => setApiKey(data.value)}
              placeholder={hasApiKey ? "••••••••••••••••••••••••••••••••" : "Enter your LSEG Open Directory API key"}
              contentBefore={<KeyRegular data-testid="domain-feed-config-key-regular" />} data-testid="domain-feed-config-api-key-input" />

            {hasApiKey &&
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px' }} data-testid="domain-feed-config-api-key-is-securely-stored-text">
                ✅ API key is securely stored in Key Vault. Enter a new key to update.
              </Text>
            }
          </Field>

          <Field label="Automatic Sync" data-testid="domain-feed-config-automatic-sync-field">
            <Switch
              checked={autoSyncEnabled}
              onChange={(_, data) => setAutoSyncEnabled(data.checked)}
              label={autoSyncEnabled ? 'Automatically sync with domain feed' : 'Manual sync only'} data-testid="domain-feed-config-auto-sync-switch" />

            {autoSyncEnabled &&
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px' }} data-testid="domain-feed-config-feed-will-be-checked-every-hour-text">
                Feed will be checked every hour for updates
              </Text>
            }
          </Field>

          <Field label="Auto-Approve New Domains" data-testid="domain-feed-config-auto-approve-new-domains-field">
            <Switch
              checked={autoApproveEnabled}
              onChange={(_, data) => setAutoApproveEnabled(data.checked)}
              label={autoApproveEnabled ? 'Automatically approve and sync new domains to policies' : 'Require manual approval for new domains'} data-testid="domain-feed-config-auto-approve-switch" />

            {autoApproveEnabled &&
            <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px', fontWeight: '600' }} data-testid="domain-feed-config-warning-new-domains-will-be-text">
                ⚠️ Warning: New domains will be automatically approved and added to policies without manual review
              </Text>
            }
            {!autoApproveEnabled &&
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px' }} data-testid="domain-feed-config-new-domains-will-require-manual-approval-text">
                New domains will require manual approval before being added to policies
              </Text>
            }
          </Field>

          <Field label="Allow Policy Updates Automatically" data-testid="domain-feed-config-allow-policy-updates-automatically-field">
            <Switch
              checked={allowPolicyUpdates}
              onChange={(_, data) => setAllowPolicyUpdates(data.checked)}
              label={allowPolicyUpdates ? 'Automatically update policies when domains change in the feed' : 'Require manual action for policy updates'} data-testid="domain-feed-config-allow-policy-updates-switch" />

            {allowPolicyUpdates &&
            <Text style={{ fontSize: '12px', color: '#D13438', marginTop: '4px', fontWeight: '600' }} data-testid="domain-feed-config-warning-policies-will-be-automatically-text">
                ⚠️ Warning: Policies will be automatically updated when domains are added or removed from the feed
              </Text>
            }
            {!allowPolicyUpdates &&
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground2, marginTop: '4px' }} data-testid="domain-feed-config-policy-updates-will-require-manual-approval-text">
                Policy updates will require manual approval when domains change in the feed
              </Text>
            }
          </Field>
        </div>

        <div className={styles.actionButtons} data-testid="domain-feed-config-action-buttons-div">
          <Button
            appearance="secondary"
            onClick={handleTestConnection}
            disabled={testing || !feedUrl || !hasApiKey && !apiKey}
            icon={<PlugConnectedRegular data-testid="domain-feed-config-plug-connected-regular" />} data-testid="domain-feed-config-test-connection-button">

            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          
          <Button
            appearance="primary"
            onClick={handleSaveConfig}
            disabled={saving || !feedUrl || !apiKey}
            icon={<SaveRegular data-testid="domain-feed-config-save-regular" />} data-testid="domain-feed-config-save-configuration-button">

            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </Card>

      {/* Environment Info */}
      <Card style={{ padding: '16px', backgroundColor: tokens.colorNeutralBackground3 }} data-testid="domain-feed-config-status-card">
        <Text style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }} data-testid="domain-feed-config-configuration-status-text">
          Configuration Status
        </Text>
        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground2 }} data-testid="domain-feed-config-status-summary-div">
          <div data-testid="domain-feed-config-status-environment-div">Environment: {process.env.NODE_ENV || 'development'}</div>
          <div data-testid="domain-feed-config-status-api-key-div">API Key Status: {hasApiKey ? '✅ Configured in Key Vault' : '❌ Not configured'}</div>
          <div data-testid="domain-feed-config-status-auto-sync-div">Auto Sync: {autoSyncEnabled ? '✅ Enabled' : '❌ Disabled'}</div>
          <div data-testid="domain-feed-config-status-auto-approve-div">Auto-Approve Domains: {autoApproveEnabled ? '⚠️ Enabled (Automatic)' : '✅ Disabled (Manual Approval)'}</div>
          <div data-testid="domain-feed-config-status-policy-updates-div">Allow Policy Updates: {allowPolicyUpdates ? '⚠️ Enabled (Automatic)' : '✅ Disabled (Manual Review)'}</div>
        </div>
        <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '8px' }} data-testid="domain-feed-config-configuration-is-managed-centrally-and-text">
          💡 Configuration is managed centrally and shared with the ADM Configuration wizard
        </Text>
      </Card>

      {/* Restricted Domains */}
      <Card className={styles.configCard} data-testid="domain-feed-config-restricted-domains-card">
        <Text style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }} data-testid="domain-feed-config-restricted-domains-list-text">
          Restricted domains list
        </Text>

        <div className={styles.restrictedDomainsSection} data-testid="domain-feed-config-restricted-domains-section-div">
          <Text className={styles.warningText} data-testid="domain-feed-config-if-there-are-any-domains-that-text">
            If there are any domains that must never be available in your organization, you can add them here. You can change this list in the future.
          </Text>

          <div className={styles.domainsList} data-testid="domain-feed-config-domains-list-div">
            {restrictedDomains.map((domain) =>
            <div key={domain} className={styles.domainItem} data-testid="domain-feed-config-domain-item-div">
                <Text data-testid="domain-feed-config-restricted-domain-text">{domain}</Text>
                <Button
                appearance="subtle"
                icon={<Delete20Regular data-testid="domain-feed-config-delete20-regular" />}
                className={styles.removeButton}
                onClick={() => handleRemoveDomain(domain)}
                aria-label={`Remove ${domain}`} data-testid="domain-feed-config-remove-domain-button" />

              </div>
            )}
          </div>

          <div className={styles.domainInput} data-testid="domain-feed-config-domain-input-div">
            <Input
              placeholder="Enter domain (e.g., example.com)"
              value={newDomain}
              onChange={(_, data) => setNewDomain(data.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddDomain();
                }
              }}
              style={{ flex: 1 }} data-testid="domain-feed-config-enter-domain-e-g-example-com-input" />

            <Button
              appearance="secondary"
              icon={<Add20Regular data-testid="domain-feed-config-add20-regular" />}
              onClick={handleAddDomain} data-testid="domain-feed-config-add-domain-button">

              Add domain
            </Button>
          </div>
        </div>
      </Card>

      {/* Domains Modal */}
      <Dialog open={showDomainsModal} onOpenChange={(_, data) => setShowDomainsModal(data.open)} data-testid="domain-feed-config-dialog">
        <DialogSurface className={styles.modalSurface} data-testid="domain-feed-config-dialog-surface">
          <DialogBody data-testid="domain-feed-config-dialog-body">
            <DialogTitle data-testid="domain-feed-config-domain-feed-test-results-dialog-title">Domain Feed Test Results</DialogTitle>
            <DialogContent className={styles.modalContent} data-testid="domain-feed-config-dialog-content">
              {loadingDomains ?
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }} data-testid="domain-feed-config-modal-loading-div">
                  <Spinner label="Loading domains from domain feed..." data-testid="domain-feed-config-loading-domains-from-domain-feed-spinner" />
                </div> :

              <>
                  {feedDomains.length > 0 &&
                <MessageBar intent="success" style={{ marginBottom: '16px' }} data-testid="domain-feed-config-results-message-bar">
                      <MessageBarBody data-testid="domain-feed-config-connection-test-successful-successfully-retrieved-message-bar-body">
                        ✅ Connection test successful! Successfully retrieved {feedDomains.length} domains from domain feed.
                      </MessageBarBody>
                    </MessageBar>
                }
                  
                  <div className={styles.domainCount} data-testid="domain-feed-config-domain-count-div">
                    <Text weight="semibold" data-testid="domain-feed-config-total-domains-in-feed-text">Total Domains in Feed:</Text>
                    <Badge appearance="filled" color="brand" size="large" data-testid="domain-feed-config-badge">
                      {feedDomains.length}
                    </Badge>
                  </div>
                  
                  <Table className={styles.domainTable} data-testid="domain-feed-config-table">
                    <TableHeader data-testid="domain-feed-config-table-header">
                      <TableRow data-testid="domain-feed-config-table-row">
                        <TableHeaderCell data-testid="domain-feed-config-domain-table-header-cell">Domain</TableHeaderCell>
                        <TableHeaderCell data-testid="domain-feed-config-organization-table-header-cell">Organization</TableHeaderCell>
                        <TableHeaderCell data-testid="domain-feed-config-status-table-header-cell">Status</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="domain-feed-config-table-body">
                      {feedDomains.map((domain, index) =>
                    <TableRow key={index} data-testid={`domain-feed-config-table-row-${index}`}>
                          <TableCell data-testid="domain-feed-config-domain-table-cell">
                            <Text className={styles.domainCell} data-testid="domain-feed-config-feed-domain-text">{domain.domain}</Text>
                          </TableCell>
                          <TableCell data-testid="domain-feed-config-organization-table-cell">{domain.organization || 'Unknown'}</TableCell>
                          <TableCell data-testid="domain-feed-config-status-table-cell">
                            <Badge appearance="filled" color="success" data-testid="domain-feed-config-verified-member-badge">
                              Verified Member
                            </Badge>
                          </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                  </Table>
                </>
              }
            </DialogContent>
            <DialogActions data-testid="domain-feed-config-dialog-actions">
              <Button appearance="secondary" onClick={() => setShowDomainsModal(false)} data-testid="domain-feed-config-close-button">
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>);

};